import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useApi } from '@/api/useApi'

import API from '@/api/apiUrls'

import { useCalculationCPTStore } from '@/stores/calculationCPT'

import { useEventsBus } from '@haskoning/vue-component-library'
import { useBaseStore } from '@haskoning/vue-basestore'
import { useSelectedItemMixin } from '@/stores/mixins/useSelectedItemMixin'

import type { CalculationType } from '@/stores/type/calculation.type'

const { emit } = useEventsBus()
const api = useApi<CalculationType>()

const baseUrl = API.CALCULATION.BASE
const processUrl = API.CALCULATION.PROCESS

type CalculationWithProcess = {
	calculation: CalculationType
	process: string
}

export const useCalculationStore = defineStore('calculation', () => {
	const baseStore = useBaseStore<CalculationType>({ service: baseUrl, api })
	const selectedItemMixin = useSelectedItemMixin<CalculationType>(baseStore)

	const createDialog = ref(false)
	const loading = ref(false)
	const filterFields = ref(['name'])
	const notReadyItems = ref<number[]>([])

	const resetAll = () => {
		baseStore.items.value = []
		baseStore.selectedItems.value = []
		baseStore.currentItem.value = null
		notReadyItems.value = []
	}

	const toggleCreateDialog = (item?: CalculationType | null, value?: boolean) => {
		if (typeof item === 'undefined') {
			baseStore.currentItem.value = {} as CalculationType
		}
		createDialog.value = value ?? !createDialog.value
	}

	const toggleNotReadyItem = (item: number) => {
		const index = notReadyItems.value.indexOf(item)
		if (index === -1) notReadyItems.value.push(item)
		else notReadyItems.value.splice(index, 1)
	}

	const pollProcess = (processId: string, calculationId: number) => {
		const url = processUrl + processId + '/'
		const interval = setInterval(async () => {
			const response = await api.service(url).get()
			if (response.status === 'SUCCEEDED') {
				clearInterval(interval)
				await updateItem(calculationId)
				emit('success', {
					status: 201,
					data: [],
					method: 'POST',
					statusText: 'Calculation was successfully completed'
				})
				toggleNotReadyItem(calculationId)
			} else if (response.status === 'FAILED') {
				clearInterval(interval)
				const calculation = getCalculationById(calculationId)
				if (calculation) removeItem(calculation)
				baseStore.currentItem.value = null
				emit('error', {
					status: 400,
					data: [],
					method: 'POST',
					statusText: 'Calculation was not successful'
				})
				toggleNotReadyItem(calculationId)
			}
		}, 3000)
	}

	const createCalculation = async (createItem: CalculationType) => {
		baseStore.writeLoading.value = true
		const url = baseUrl + 'run_task/'
		const responseData = (await api
			.service(url)
			.create(createItem)) as unknown as CalculationWithProcess
		const calculation = responseData.calculation
		toggleNotReadyItem(calculation.id)
		pollProcess(responseData.process, calculation.id)
		baseStore.items.value.push(calculation)
		emit('success', {
			status: 202,
			data: [],
			method: 'POST',
			statusText: 'A background task is running to generate your calculation results.'
		})
		baseStore.writeLoading.value = false
	}

	const updateItem = async (updateItemId: number) => {
		baseStore.writeLoading.value = true
		const calculation = (await baseStore.fetchItem(updateItemId)) as CalculationType
		baseStore.currentItem.value = null
		if (calculation) {
			baseStore.items.value = baseStore.items.value.map((item) =>
				item.id === updateItemId ? calculation : item
			)
		}
		baseStore.writeLoading.value = false
	}

	const removeItem = (item: CalculationType) => {
		baseStore.items.value = baseStore.items.value.filter((i) => i.id !== item.id)
	}

	const removeItemsByCollectionId = (collectionId: number) => {
		const removeItems = baseStore.items.value.filter(
			(item) => (item as any).gi_data_upload_collection === collectionId
		)
		removeItems.forEach((item) => removeItem(item))
	}

	const toggleChildSelectedItems = (id: number) => {
		const calculationCPTStore = useCalculationCPTStore()
		const item = baseStore.items.value.find((item) => item.id === id)
		const index = baseStore.selectedItems.value.findIndex((selectedItem) => selectedItem.id === id)
		if (item) {
			if (index !== -1) {
				calculationCPTStore.selectAllItemsByCalculationId(item.id)
			} else {
				calculationCPTStore.clearSelectedItemsByCalculationId(item.id)
			}
		}
	}

	const selectAllItems = () => {
		baseStore.selectedItems.value = [...baseStore.items.value]
		const calculationCPTStore = useCalculationCPTStore()
		baseStore.selectedItems.value.forEach((item) => {
			calculationCPTStore.selectAllItemsByCalculationId(item.id)
		})
	}

	const clearSelectedItems = () => {
		baseStore.selectedItems.value = []
		const calculationCPTStore = useCalculationCPTStore()
		calculationCPTStore.clearSelectedItems()
	}

	const getCalculationById = (id: number): CalculationType | null => {
		return baseStore.items.value.find((item) => item.id === id) ?? null
	}

	const fieldsFiltered = computed(() => {
		return baseStore.fieldsList.value.filter((field) =>
			filterFields.value.includes(field.parameter)
		)
	})

	return {
		...baseStore,
		...selectedItemMixin,
		createDialog,
		loading,
		filterFields,
		notReadyItems,
		fieldsFiltered,
		resetAll,
		toggleCreateDialog,
		toggleNotReadyItem,
		pollProcess,
		createCalculation,
		updateItem,
		removeItem,
		removeItemsByCollectionId,
		toggleChildSelectedItems,
		selectAllItems,
		clearSelectedItems,
		getCalculationById
	}
})
